// Still filling (DESIGN_SPEC §11.4.12, §12.12; the live cockpit's last cell, s4 side): what each section of the finished
// report holds now, and what it gains at collection.
//   <StillFilling ctx no />   section · now · at collection
import type { ReactNode } from "react";
import { FigHead } from "@/components/ui";
import { pickArtifact } from "@/hooks";
import { backgroundIoc } from "@/lib/derive/noise";
import { count, plural } from "@/lib/format";
import type { ReportCtx } from "@/lib/derive/types";

type Row = { section: string; now: ReactNode; later: string };

const faint = (s: string) => <span className="faint">{s}</span>;

/** Indicators seen so far that are not Windows background. */
const IOC_COUNTS = new WeakMap<object, { noise: ReportCtx["noise"]; n: number }>();

/** Indicators the stream extracted so far, OS background left out (once per snapshot). */
export function liveIocCount(ctx: ReportCtx): number {
  const snap = ctx.stream.snap;
  const hit = IOC_COUNTS.get(snap);
  if (hit && hit.noise === ctx.noise) return hit.n;
  let n = 0;
  for (const i of snap.iocs.values()) if (!backgroundIoc(i.kind, i.value, ctx.noise)) n++;
  IOC_COUNTS.set(snap, { noise: ctx.noise, n });
  return n;
}

export function StillFilling({ ctx, no }: { ctx: ReportCtx; no: number }) {
  const { stream, artifacts, job } = ctx;
  const iocs = liveIocCount(ctx);
  const dumps = artifacts.filter((a) => a.kind === "process-dump").length;
  const frames = artifacts.filter((a) => a.kind === "screenshot").length;
  const staticArtifact = pickArtifact(artifacts, "static-report");
  const rows: Row[] = [
    { section: "Behavior", now: faint("—"), later: "scored tags and the verdict" },
    { section: "Indicators", now: iocs ? `${count(iocs)} so far` : faint("none yet"), later: "deduplicated, with sources" },
    {
      section: "Static PE",
      now: job.kind === "url" ? faint("not a file") : ctx.staticReport ? "ready" : staticArtifact ? "parsing" : faint("not a PE file"),
      later: job.kind === "url" ? "—" : "the same",
    },
    {
      section: "Artifacts",
      now: artifacts.length ? (dumps ? `${count(dumps)} ${plural(dumps, "dump")} · ${count(artifacts.length)} files` : `${count(artifacts.length)} ${plural(artifacts.length, "file")}`) : faint("none yet"),
      later: "+ pcap, memory, report",
    },
    { section: "Session", now: stream.live ? `live${frames ? ` · ${count(frames)} ${plural(frames, "frame")}` : ""}` : faint("—"), later: "replay video and frames" },
  ];
  return (
    <section className="cell s4 side fig" aria-labelledby={`fig-${no}-t`}>
      <FigHead no={no} label="Still filling" count="at collection" />
      <div className="fig-body mt-4 tscroll">
        <table className="t t--sm">
          <thead>
            <tr>
              <th>section</th>
              <th>now</th>
              <th>at collection</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.section}>
                <td className="primary">{r.section}</td>
                <td>{r.now}</td>
                <td className="whitespace-normal">{r.later}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
